
import { defineComponent, PropType } from 'vue'

import ExportRunAsMarkdownQuery from '~/apollo/queries/repository/runs/run/exportAsMarkdown.gql'
import { getRunProps } from '~/utils/runs'
import { gateStatusIconSrc } from '~/utils/ui'

import { type Check, type Run, type ScaCheck } from '~/types/types'
import type { GraphqlQueryResponse } from '~/types/apollo-graphql-types'

export default defineComponent({
  name: 'CheckLayoutWrapper',
  props: {
    run: {
      type: Object as PropType<Run>,
      required: true
    },
    checks: {
      type: Array as PropType<Check[]>,
      required: true
    },
    scaChecks: {
      type: Array as PropType<ScaCheck[]>,
      default: () => []
    },
    canRetryAnalysis: {
      type: Boolean,
      default: false
    },
    retryableChecks: {
      type: Array as PropType<Check[]>,
      default: () => []
    },
    analyzersInRetry: {
      type: Set as PropType<Set<string>>,
      default: () => new Set()
    },
    flashActiveAnalyzer: {
      type: Boolean,
      default: false
    },
    branchRuns: {
      type: Array as PropType<Run[]>,
      default: () => []
    },
    showArchivedRunEmptyState: {
      type: Boolean,
      default: false
    },
    showSkipEmptyState: {
      type: Boolean,
      default: false
    },
    provider: {
      type: String,
      required: true
    }
  },
  emits: ['retry-checks'],
  data() {
    return {
      hasCopiedMarkdown: false,
      isCopyingMarkdown: false
    }
  },
  computed: {
    iconSrc(): string {
      return gateStatusIconSrc(this.run.status)
    },
    runProps() {
      return getRunProps(this.run)
    },
    pillIcon(): string {
      return this.$providerMetaMap[this.provider]?.icon ?? ''
    },
    retryableAnalyzers(): string[] {
      return this.retryableChecks
        .map((check) => check.analyzer?.shortcode)
        .filter(Boolean) as string[]
    },
    areAllChecksRetrying(): boolean {
      return this.retryableChecks.every((check) =>
        this.analyzersInRetry.has(check.analyzer?.shortcode || '')
      )
    },
    isFreshLoad(): boolean {
      return this.$route.params.runId !== this.run.runId
    },
    hasReportCard(): boolean {
      return !!this.run.reportCard
    },
    isReportCardActive(): boolean {
      return this.$route.name === 'provider-owner-repo-run-runId' && this.hasReportCard
    },
    reportCardRoute(): string {
      const { provider, owner, repo, runId } = this.$route.params
      return `/${provider}/${owner}/${repo}/run/${runId}`
    },
    copyMarkdownIcon(): string {
      if (this.isCopyingMarkdown) return 'spin-loader'
      return this.hasCopiedMarkdown ? 'check' : 'square-m'
    },
    copyMarkdownLabel(): string {
      if (this.isCopyingMarkdown) return 'Copying...'
      return this.hasCopiedMarkdown ? 'Copied!' : 'Copy as Markdown'
    }
  },
  methods: {
    async copyAsMarkdown(): Promise<void> {
      if (this.isCopyingMarkdown) return
      this.isCopyingMarkdown = true
      try {
        const response = (await this.$fetchGraphqlData(ExportRunAsMarkdownQuery, {
          runId: this.run.runId,
          analyzerShortcode: '',
          targetId: ''
        })) as GraphqlQueryResponse
        const markdown = response.data.exportRunAsMarkdown
        if (markdown) {
          await this.$copyToClipboard(markdown)
          this.hasCopiedMarkdown = true
          setTimeout(() => {
            this.hasCopiedMarkdown = false
          }, 1000)
        }
      } catch (e) {
        this.$toast.danger('Failed to copy run as markdown.')
      } finally {
        this.isCopyingMarkdown = false
      }
    }
  }
})
